import React, { createContext, useContext, useState, ReactNode, useEffect, useRef, useCallback } from 'react';
import { useCognitoAuth } from './CognitoAuthContext';
import { getUserIdAsNumber } from '../utils/cognitoUtils';
import apiService from '../services/api';

export type BusinessArea = 'telecommunications' | 'energy_solutions';

interface AppContextType {
  currentBusinessArea: BusinessArea;
  setCurrentBusinessArea: (area: BusinessArea) => void;
  sidebarCollapsed: boolean;
  setSidebarCollapsed: (collapsed: boolean) => void;
  notifications: any[];
  addNotification: (notification: any) => void;
  clearNotifications: () => void;
  markNotificationAsRead: (id: string) => void;
  fetchNotifications: () => Promise<void>;
  dismissNotifications: (scope: 'new' | 'all') => Promise<void>;
  loading: boolean;
  refreshCalendarData: () => void;
  onCalendarDataRefresh: (callback: () => void) => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export const useApp = () => {
  const context = useContext(AppContext);
  if (context === undefined) {
    throw new Error('useApp must be used within an AppProvider');
  }
  return context;
};

interface AppProviderProps {
  children: ReactNode;
}

export const AppProvider: React.FC<AppProviderProps> = ({ children }) => {
  const { user } = useCognitoAuth();
  const [currentBusinessArea, setCurrentBusinessArea] = useState<BusinessArea>('telecommunications');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const hasInitialized = useRef(false);
  const calendarRefreshCallbacks = useRef<(() => void)[]>([]);

  // Set initial business area only once when user first loads
  useEffect(() => {
    if (user?.business_area && !hasInitialized.current) {
      const userBusinessArea = user.business_area as BusinessArea;
      setCurrentBusinessArea(userBusinessArea);
      hasInitialized.current = true;
    }
  }, [user]);

  const userId = user ? (user.id ?? user.user_id) : null;

  const fetchNotifications = useCallback(async () => {
    if (!user || !userId) return;

    const userTenant = localStorage.getItem('user_tenant');
    if (userTenant === 'partnership') return;

    setLoading(true);
    try {
      const data = await apiService.getNotifications(getUserIdAsNumber(user));
      setNotifications(data || []);
    } catch (error) {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  }, [user, userId]);

  // Fetch notifications when user id changes (stable dependency, avoids re-runs on object reference change)
  useEffect(() => {
    if (userId) {
      fetchNotifications();
    } else {
      setNotifications([]);
      hasInitialized.current = false;
    }
  }, [userId, fetchNotifications]);

  // Poll for new notifications every 30 seconds
  useEffect(() => {
    if (!userId) return;
    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000);
    return () => clearInterval(interval);
  }, [userId, fetchNotifications]);

  const addNotification = (notification: any) => {
    setNotifications(prev => [notification, ...prev]);
  };

  const clearNotifications = () => {
    setNotifications([]);
  };

  const dismissNotifications = useCallback(async (scope: 'new' | 'all') => {
    if (!user) return;
    await apiService.clearNotifications(getUserIdAsNumber(user), scope);
    await fetchNotifications();
  }, [user, fetchNotifications]);

  const markNotificationAsRead = (id: string) => {
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === id ? { ...notif, read: true } : notif
      )
    );
  };

  const refreshCalendarData = () => {
    calendarRefreshCallbacks.current.forEach(callback => callback());
  };

  const onCalendarDataRefresh = (callback: () => void) => {
    calendarRefreshCallbacks.current.push(callback);
    return () => {
      const index = calendarRefreshCallbacks.current.indexOf(callback);
      if (index > -1) {
        calendarRefreshCallbacks.current.splice(index, 1);
      }
    };
  };

  const value: AppContextType = {
    currentBusinessArea,
    setCurrentBusinessArea,
    sidebarCollapsed,
    setSidebarCollapsed,
    notifications,
    addNotification,
    clearNotifications,
    markNotificationAsRead,
    fetchNotifications,
    dismissNotifications,
    loading,
    refreshCalendarData,
    onCalendarDataRefresh,
  };

  return (
    <AppContext.Provider value={value}>
      {children}
    </AppContext.Provider>
  );
}; 